import { useMemo } from 'react';

import ThemeProvider from '@toolbox/style/theme/common/ThemeProvider';
import { useMarketTrends } from '@widget/core';
import { Icon } from '@widget/toolbox/components';
import { IconWrap, Description, Value, ContentWrap, Wrap } from './styles';

import type { RegionalTrendSchema } from '@widget/storyblok/schemas';
import type { z } from 'zod';

type RegionalTrendProps = z.infer<typeof RegionalTrendSchema>;

export const RegionalTrend = ({ trend, icon, description }: RegionalTrendProps) => {
  const { marketTrends } = useMarketTrends();

  const formatTrendValue = useMemo(() => {
    if (!trend) return undefined;

    const value = marketTrends?.[trend];
    if (value === undefined) return undefined;

    const formatter = new Intl.NumberFormat('de-DE');
    const formatters: Record<string, (value: number) => string> = {
      averageSqmPrice: (value) => `€${formatter.format(Math.round(value))}`,
      averageSellingTime: (value) => `${value} Tage`,
      quarterSqmPriceTrend: (value) => `${(value * 100).toFixed(2)}%`,
      brokerCount: (value) => `${value}`,
      propertiesSold: (value) => `${value}`,
    };

    return formatters[trend] ? formatters[trend](value) : value.toString();
  }, [trend, marketTrends]);

  return (
    <ThemeProvider>
      <Wrap>
        <IconWrap>{icon && <Icon type={icon} />}</IconWrap>
        <ContentWrap>
          <Value>{formatTrendValue}</Value>
          <Description>{description}</Description>
        </ContentWrap>
      </Wrap>
    </ThemeProvider>
  );
};
